import { usePathname, useRouter } from 'expo-router'
import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useOnboarding } from '@/contexts/OnboardingContext'

const STEP_INDEX: Record<string, number> = {
  '/onboarding': 0,
  '/onboarding/destination': 1,
  '/onboarding/timeline': 2,
  '/onboarding/origin': 3,
  '/onboarding/age': 4,
  '/onboarding/savings': 5,
  '/onboarding/situation': 6,
  '/onboarding/summary': 7,
  '/onboarding/video': 8,
}

function getFirstIncompleteStep(ctx: ReturnType<typeof useOnboarding>): string {
  if (!ctx.goal) return '/onboarding'
  if (!ctx.destination) return '/onboarding/destination'
  if (!ctx.timeline) return '/onboarding/timeline'
  if (!ctx.originCity) return '/onboarding/origin'
  if (!ctx.age) return '/onboarding/age'
  if (!ctx.savings) return '/onboarding/savings'
  if (!ctx.situation) return '/onboarding/situation'
  return ''
}

/**
 * Guards the current onboarding step.
 * While hydration is in progress, isReady is false.
 * Once loaded, redirects only if the user is AHEAD of the first incomplete
 * step (skipping forward). Going back to a previous step is always allowed.
 */
export function useOnboardingGuard(): { isReady: boolean } {
  const ctx = useOnboarding()
  const { isAuthenticated, isLoading, user } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [isReady, setIsReady] = useState(false)

  // Redirect approved users away from onboarding to community
  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.application_status === 'approved') {
      router.replace('/community' as never)
    }
  }, [isLoading, isAuthenticated, user?.application_status, router])

  useEffect(() => {
    if (!ctx.isLoaded) return

    const firstIncomplete = getFirstIncompleteStep(ctx)
    if (firstIncomplete) {
      const currentIndex = STEP_INDEX[pathname] ?? -1
      const firstIncompleteIndex = STEP_INDEX[firstIncomplete] ?? 0
      if (currentIndex > firstIncompleteIndex) {
        router.replace(firstIncomplete as never)
        return
      }
    }

    setIsReady(true)
  }, [ctx, pathname, router])

  return { isReady }
}
